import { supabase } from './supabase'
import { parseAmountToCents } from './money'
import { validateIban } from './validators/iban'
import {
  formatReference,
  validateStructuredCommunication,
} from './validators/structuredCommunication'
import { checkManual, type ManualPayment } from './manualPayment'

export { checkManual } from './manualPayment'
export type { ManualPayment, ManualProblem } from './manualPayment'

/**
 * Adding something by hand.
 *
 * Not everything arrives as a photo: a payment agreed on the phone, a bill
 * that only exists in an email, a deadline someone mentioned. spec.md §354.
 *
 * The important difference from the pipeline: a human typed this, so it is
 * verified by definition. That is what lets an IBAN be stored at all — the
 * check constraint in 0001 refuses one without `verified_at`, deliberately,
 * so that nothing unverified can ever reach a payment code (rule 14).
 */
export async function createManualPayment(
  input: ManualPayment,
  /** Unused — the database reads auth.uid(). Kept so callers need not change. */
  _userId?: string,
): Promise<string> {
  const problems = checkManual(input)
  if (problems.length > 0) throw new Error(problems[0]!.message)

  const cents = parseAmountToCents(input.amount)!
  const ibanCheck = validateIban(input.iban)
  const refCheck = validateStructuredCommunication(input.reference)

  // One statement, one transaction.
  //
  // This used to be three: the obligation, then the instalment, then the log
  // row. Dying between the first two left an obligation with no instalment —
  // and rule 5 says the instalment is the payable unit, so that invoice is
  // real, marked verified, and invisible to everybody. It would never be paid
  // and never be noticed. See migration 0045.
  const { data, error } = await supabase.rpc('create_manual_obligation', {
    p_space_id: input.spaceId,
    p_description: input.description,
    p_amount_cents: cents,
    p_due_date: input.dueDate,
    p_category: input.category,
    p_iban: ibanCheck.valid ? ibanCheck.canonical : null,
    p_payment_reference: input.reference.trim()
      ? refCheck.valid
        ? formatReference(refCheck.canonical)
        : input.reference.trim()
      : null,
    p_reference_canonical: refCheck.valid ? refCheck.canonical : null,
    p_notes: input.notes,
  })
  if (error) throw error

  return data as string
}
